import apiClient from '../client'
import type { ApiResponse } from '../client'

/**
 * 文档管理 API 服务
 */

// 文档数据类型
export interface MarkdownDocument {
  id: number
  slug: string
  language: string
  category: string
  title: string
  description?: string
  markdown_content: string
  html_content?: string
  seo_title?: string
  seo_description?: string
  seo_keywords?: string
  is_published: boolean
  created_at: string
  updated_at: string
}

// 文档列表项类型
export interface DocumentListItem {
  id: number
  slug: string
  title: string
  category: string
  description?: string
  url: string
  updated_at: string
}

// 创建文档表单数据
export interface CreateDocumentForm {
  slug: string
  language: string
  category: string
  title: string
  description?: string
  seo_title?: string
  seo_description?: string
  seo_keywords?: string
  is_published: boolean
  markdown_file: File
}

// 更新文档表单数据
export interface UpdateDocumentForm {
  title?: string
  description?: string
  seo_title?: string
  seo_description?: string
  seo_keywords?: string
  is_published?: boolean
  markdown_file?: File
}

class DocumentsApi {
  /**
   * 获取文档列表（管理员视图，包含未发布的）
   */
  async getDocumentList(params?: {
    page?: number
    page_size?: number
    language?: string
    category?: string
    search?: string
  }): Promise<ApiResponse<MarkdownDocument[]>> {
    const response = await apiClient.get('/api/v1/admin/documents', { params })
    return response.data
  }

  /**
   * 获取单个文档详情
   */
  async getDocument(id: number): Promise<ApiResponse<MarkdownDocument>> {
    const response = await apiClient.get(`/api/v1/admin/documents/${id}`)
    return response.data
  }

  /**
   * 创建文档
   */
  async createDocument(data: CreateDocumentForm): Promise<ApiResponse<MarkdownDocument>> {
    const formData = new FormData()

    formData.append('slug', data.slug)
    formData.append('language', data.language)
    formData.append('category', data.category)
    formData.append('title', data.title)
    if (data.description) formData.append('description', data.description)
    if (data.seo_title) formData.append('seo_title', data.seo_title)
    if (data.seo_description) formData.append('seo_description', data.seo_description)
    if (data.seo_keywords) formData.append('seo_keywords', data.seo_keywords)
    formData.append('is_published', String(data.is_published))
    formData.append('markdown_file', data.markdown_file)

    const response = await apiClient.post('/api/v1/admin/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }

  /**
   * 更新文档
   */
  async updateDocument(id: number, data: UpdateDocumentForm): Promise<ApiResponse<MarkdownDocument>> {
    const formData = new FormData()

    if (data.title) formData.append('title', data.title)
    if (data.description !== undefined) formData.append('description', data.description || '')
    if (data.seo_title !== undefined) formData.append('seo_title', data.seo_title || '')
    if (data.seo_description !== undefined) formData.append('seo_description', data.seo_description || '')
    if (data.seo_keywords !== undefined) formData.append('seo_keywords', data.seo_keywords || '')
    if (data.is_published !== undefined) formData.append('is_published', String(data.is_published))
    if (data.markdown_file) formData.append('markdown_file', data.markdown_file)

    const response = await apiClient.put(`/api/v1/admin/documents/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }

  /**
   * 删除文档
   */
  async deleteDocument(id: number): Promise<ApiResponse<null>> {
    const response = await apiClient.delete(`/api/v1/admin/documents/${id}`)
    return response.data
  }

  /**
   * 发布/下线文档
   */
  async togglePublish(id: number, isPublished: boolean): Promise<ApiResponse<MarkdownDocument>> {
    const response = await apiClient.patch(`/api/v1/admin/documents/${id}/publish`, {
      is_published: isPublished
    })
    return response.data
  }

  /**
   * 获取公开文档列表
   */
  async getPublicDocumentList(language: string, category?: string): Promise<DocumentListItem[]> {
    const params: any = {}
    if (category) params.category = category

    const response = await apiClient.get(`/api/v1/${language}/docs/list`, { params })
    return response.data
  }
}

export const documentsApi = new DocumentsApi()
